import React from 'react';
import { useTranslation } from 'react-i18next';
import type { RentDetail } from '../types/apartments';

interface RentDetailLineProps {
  detail: RentDetail;
  index: number;
  isReadOnly: boolean;
  isRemovable: boolean;
  isValueReadOnly?: boolean;
  validationError?: string;
  onUpdateTitle: (value: string) => void;
  onUpdateValue: (value: string | number) => void;
  onRemove: () => void;
}

export const RentDetailLine: React.FC<RentDetailLineProps> = ({
  detail,
  index,
  isReadOnly,
  isRemovable,
  isValueReadOnly = false,
  validationError,
  onUpdateTitle,
  onUpdateValue,
  onRemove,
}) => {
  const { t } = useTranslation();

  const handleValueChange = (inputValue: string) => {
    // Replace comma/colon with dot
    inputValue = inputValue.replace(/[,:]/g, '.');
    
    // Only allow numbers, one dot, and minus sign at the start
    inputValue = inputValue.replace(/[^0-9.\-]/g, '');
    
    // Ensure minus sign is only at the start and only one
    const hasLeadingMinus = inputValue.startsWith('-');
    inputValue = inputValue.replace(/-/g, '');
    if (hasLeadingMinus) {
      inputValue = '-' + inputValue;
    }
    
    // Prevent multiple dots
    const dotCount = (inputValue.match(/\./g) || []).length;
    if (dotCount > 1) {
      const firstDotIndex = inputValue.indexOf('.');
      inputValue = inputValue.substring(0, firstDotIndex + 1) + inputValue.substring(firstDotIndex + 1).replace(/\./g, '');
    }
    
    // Prevent leading zeros (except 0. or just 0 or -0)
    const numericPart = hasLeadingMinus ? inputValue.substring(1) : inputValue;
    if (numericPart.length > 1 && numericPart[0] === '0' && numericPart[1] !== '.') {
      inputValue = (hasLeadingMinus ? '-' : '') + numericPart.substring(1);
    }
    
    // Limit to 2 decimal places
    if (inputValue.includes('.')) {
      const parts = inputValue.split('.');
      if (parts[1] && parts[1].length > 2) {
        inputValue = parts[0] + '.' + parts[1].substring(0, 2);
      }
    }
    
    // Allow intermediate states like "70.", "-", "-0", or empty string
    if (inputValue === '' || inputValue === '.' || inputValue === '0' || inputValue === '-' || inputValue === '-0') {
      onUpdateValue(inputValue);
    } else if (inputValue.endsWith('.')) {
      // Keep the dot for continued typing
      onUpdateValue(inputValue);
    } else {
      // Parse to number for complete values
      const numValue = parseFloat(inputValue);
      if (!isNaN(numValue)) {
        onUpdateValue(inputValue);
      }
    }
  };

  const handleValueBlur = (value: string) => {
    // Clean up trailing dots, single minus, or convert empty to 0
    if (value === '' || value === '.' || value === '0' || value === '-' || value === '-0') {
      onUpdateValue(0);
    } else if (value.endsWith('.')) {
      // Remove trailing dot and convert to number
      const numValue = parseFloat(value);
      onUpdateValue(numValue);
    }
  };

  return (
    <div className="flex gap-2 items-start">
      <div className="flex-1">
        <input
          type="text"
          placeholder={t('pdfModal.lineTitle')}
          value={detail.title}
          onChange={(e) => onUpdateTitle(e.target.value)}
          readOnly={isReadOnly}
          required={!isReadOnly}
          className={
            isReadOnly 
              ? "w-full rounded-md border p-2 text-sm bg-gray-100 border-gray-300 text-gray-700 cursor-not-allowed"
              : validationError
              ? "w-full rounded-md border border-red-500 p-2 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
              : "w-full rounded-md border border-gray-300 p-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
          }
        />
        {validationError && (
          <p className="text-xs text-red-600 mt-1">{validationError}</p>
        )}
      </div>
      <div className="w-24">
        <input
          type="text"
          inputMode="decimal"
          placeholder="0.00"
          value={detail.value}
          onChange={(e) => handleValueChange(e.target.value)}
          onBlur={(e) => handleValueBlur(e.target.value)}
          readOnly={isValueReadOnly}
          className={
            isValueReadOnly
              ? "w-full rounded-md border p-2 text-sm bg-gray-100 border-gray-300 text-gray-700 cursor-not-allowed"
              : "w-full rounded-md border border-gray-300 p-2 text-sm focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-400"
          }
        />
      </div>
      <button
        type="button"
        onClick={onRemove}
        disabled={!isRemovable}
        className="p-2 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        title={!isRemovable ? t('pdfModal.cannotRemoveRequired') : t('pdfModal.removeLine')}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    </div>
  );
};

